export const CALL_CONTROL_NAME = 'End call';
export const CALL_MIN_VARIABLE = '--la-call-min';
export const DOCK_BAR_VARIABLE = '--la-dock-bar';
export const CALL_FRAME_ATTRIBUTE = 'data-la-call';

const accessibleName = (element: Element): string =>
  (element.getAttribute('aria-label') ?? element.textContent ?? '').replace(/\s+/g, ' ').trim();

export const callControls = (callView: HTMLElement): HTMLElement[] =>
  Array.from(callView.querySelectorAll<HTMLElement>('button, [role="button"]')).filter(
    control => accessibleName(control) === CALL_CONTROL_NAME,
  );

const clipsVertically = (element: Element): boolean =>
  window.getComputedStyle(element).overflowY !== 'visible';

const inOverlay = (element: Element, callView: Element): boolean => {
  for (
    let node: Element | null = element;
    node !== null && node !== callView;
    node = node.parentElement
  ) {
    if (node.getAttribute('role') === 'dialog') return true;
    if (window.getComputedStyle(node).position === 'fixed') return true;
  }
  return false;
};

const visibleBottom = (node: HTMLElement, callView: HTMLElement): number => {
  const rect = node.getBoundingClientRect();
  const padding =
    node === callView ? parseFloat(window.getComputedStyle(node).paddingBottom) || 0 : 0;
  return rect.top + node.clientTop + node.clientHeight - padding;
};

export const callControlClip = (callView: HTMLElement): number => {
  const controls = callControls(callView).filter(
    control => control.getBoundingClientRect().height > 0 && !inOverlay(control, callView),
  );
  let clip = 0;
  controls.forEach(control => {
    const bottom = control.getBoundingClientRect().bottom;
    for (let node = control.parentElement; node !== null; node = node.parentElement) {
      if (node === callView || clipsVertically(node)) {
        clip = Math.max(clip, bottom - visibleBottom(node, callView));
      }
      if (node === callView) break;
    }
  });
  return Math.ceil(clip);
};

export const hasCallControl = (callView: HTMLElement): boolean => callControls(callView).length > 0;

export const reservedCallHeight = (
  callViewHeight: number,
  clip: number,
  frameHeight: number,
  barHeight: number,
): number =>
  Math.max(
    0,
    Math.min(Math.ceil(callViewHeight + Math.max(0, clip)), Math.floor(frameHeight - barHeight)),
  );

export const DOCK_BAR_FLOOR = 40;

export const dockBarHeight = (dock: Element | null): number => {
  if (!(dock instanceof HTMLElement)) return 0;
  const bar = dock.querySelector('[aria-expanded]')?.parentElement;
  const height =
    bar instanceof HTMLElement && bar !== dock ? bar.getBoundingClientRect().height : 0;
  return height > 0 ? Math.ceil(height + dock.clientTop) : DOCK_BAR_FLOOR;
};
